import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  AlertTriangle, CheckCircle, XCircle, Pill, Search, RefreshCw,
  Shield, Activity, Users, Clock, FileText, ChevronRight, Info, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MedicationInteractionChecker = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showCheckDialog, setShowCheckDialog] = useState(false);
  const [checkResults, setCheckResults] = useState(null);
  const [checkForm, setCheckForm] = useState({
    patient_id: '',
    patient_name: '',
    current_medications: [],
    new_medication: ''
  });
  const [newMedInput, setNewMedInput] = useState('');
  const [drugs, setDrugs] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchDrugs();
    fetchAlerts();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase60/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      } else {
        setDashboardData(generateSampleData());
      }
    } catch (error) {
      console.error('Error:', error);
      setDashboardData(generateSampleData());
    } finally {
      setLoading(false);
    }
  };

  const fetchDrugs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase60/drugs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDrugs(data.drugs || []);
      }
    } catch (error) {
      console.error('Error fetching drugs:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase60/alerts?status=active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const generateSampleData = () => ({
    stats: {
      total_checks: 245,
      interactions_found: 67,
      major_interactions: 12,
      checks_today: 23,
      alert_rate: 27.3
    },
    recent_checks: [
      { id: '1', patient_name: 'John Smith', new_medication: 'Morphine', interactions_found: 2, max_severity: 'major', created_at: new Date().toISOString() },
      { id: '2', patient_name: 'Mary Johnson', new_medication: 'Lorazepam', interactions_found: 1, max_severity: 'moderate', created_at: new Date(Date.now() - 3600000).toISOString() },
      { id: '3', patient_name: 'Robert Davis', new_medication: 'Gabapentin', interactions_found: 0, max_severity: 'none', created_at: new Date(Date.now() - 7200000).toISOString() }
    ],
    high_risk_patients: [
      { patient_name: 'John Smith', severity: 'major', drugs_involved: ['Morphine', 'Midazolam'], status: 'active' },
      { patient_name: 'Alice Brown', severity: 'major', drugs_involved: ['Fentanyl', 'Lorazepam'], status: 'active' }
    ],
    severity_breakdown: { major: 12, moderate: 35, minor: 20 }
  });

  const checkInteractions = async () => {
    if (!checkForm.new_medication || checkForm.current_medications.length === 0) {
      toast.error('Please add current medications and a new medication to check');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase60/check`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_id: checkForm.patient_id || 'patient_' + Date.now(),
          patient_name: checkForm.patient_name || 'Test Patient',
          current_medications: checkForm.current_medications,
          new_medication: checkForm.new_medication
        })
      });

      if (response.ok) {
        const result = await response.json();
        setCheckResults(result);
        if (result.interactions.length > 0) {
          toast.warning(`Found ${result.interactions.length} interaction(s)!`);
        } else {
          toast.success('No interactions detected');
        }
        fetchDashboard();
        fetchAlerts();
      } else {
        toast.error('Failed to check interactions');
      }
    } catch (error) {
      toast.error('Error checking interactions');
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase60/alerts/${alertId}/acknowledge`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note: 'Reviewed and acknowledged' })
      });

      if (response.ok) {
        toast.success('Alert acknowledged');
        fetchAlerts();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Error acknowledging alert');
    }
  };

  const addCurrentMedication = () => {
    if (newMedInput && !checkForm.current_medications.includes(newMedInput)) {
      setCheckForm({
        ...checkForm,
        current_medications: [...checkForm.current_medications, newMedInput]
      });
      setNewMedInput('');
    }
  };

  const removeMedication = (med) => {
    setCheckForm({
      ...checkForm,
      current_medications: checkForm.current_medications.filter(m => m !== med)
    });
  };

  const getSeverityBadge = (severity) => {
    const config = {
      major: { color: 'bg-red-600 text-white', icon: AlertTriangle },
      moderate: { color: 'bg-orange-500 text-white', icon: AlertCircle },
      minor: { color: 'bg-yellow-500 text-black', icon: Info },
      none: { color: 'bg-green-500 text-white', icon: CheckCircle }
    };
    const cfg = config[severity] || config.none;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} flex items-center gap-1 font-semibold`}>
        <Icon className="h-3 w-3" />
        {severity.toUpperCase()}
      </Badge>
    );
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-medium">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1 font-medium">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="medication-interaction-checker">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Medication Interaction Checker</h1>
              <p className="text-sm text-slate-600 mt-1 font-medium">Real-time drug interaction alerts and analysis</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={fetchDashboard}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setShowCheckDialog(true)}>
                <Search className="h-4 w-4 mr-2" />
                Check Interactions
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="font-semibold">Dashboard</TabsTrigger>
            <TabsTrigger value="alerts" className="font-semibold">Active Alerts</TabsTrigger>
            <TabsTrigger value="database" className="font-semibold">Drug Database</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard title="Total Checks" value={dashboardData?.stats?.total_checks || 0} icon={Activity} color="blue" />
              <StatCard title="Checks Today" value={dashboardData?.stats?.checks_today || 0} icon={Clock} color="green" />
              <StatCard title="Interactions Found" value={dashboardData?.stats?.interactions_found || 0} icon={AlertTriangle} color="orange" />
              <StatCard title="Major Interactions" value={dashboardData?.stats?.major_interactions || 0} icon={XCircle} color="red" />
              <StatCard title="Alert Rate" value={`${dashboardData?.stats?.alert_rate || 0}%`} icon={Shield} color="purple" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Severity Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-900 font-bold">Interaction Severity Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full bg-red-600" />
                        <span className="font-semibold text-slate-900">Major</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div className="bg-red-600 h-2 rounded-full" style={{ width: `${(dashboardData?.severity_breakdown?.major || 0) / (dashboardData?.stats?.interactions_found || 1) * 100}%` }} />
                        </div>
                        <span className="text-sm font-bold text-slate-900 w-8">{dashboardData?.severity_breakdown?.major || 0}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full bg-orange-500" />
                        <span className="font-semibold text-slate-900">Moderate</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div className="bg-orange-500 h-2 rounded-full" style={{ width: `${(dashboardData?.severity_breakdown?.moderate || 0) / (dashboardData?.stats?.interactions_found || 1) * 100}%` }} />
                        </div>
                        <span className="text-sm font-bold text-slate-900 w-8">{dashboardData?.severity_breakdown?.moderate || 0}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full bg-yellow-500" />
                        <span className="font-semibold text-slate-900">Minor</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${(dashboardData?.severity_breakdown?.minor || 0) / (dashboardData?.stats?.interactions_found || 1) * 100}%` }} />
                        </div>
                        <span className="text-sm font-bold text-slate-900 w-8">{dashboardData?.severity_breakdown?.minor || 0}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* High Risk Patients */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-900 font-bold">High Risk Patients</CardTitle>
                  <CardDescription className="text-slate-600 font-medium">Patients with major drug interactions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData?.high_risk_patients?.slice(0, 5).map((patient, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                        <div>
                          <p className="font-semibold text-slate-900">{patient.patient_name}</p>
                          <p className="text-sm text-slate-600 font-medium">{patient.drugs_involved?.join(' + ')}</p>
                        </div>
                        {getSeverityBadge(patient.severity)}
                      </div>
                    ))}
                    {(!dashboardData?.high_risk_patients || dashboardData?.high_risk_patients?.length === 0) && (
                      <p className="text-center text-slate-500 py-4 font-medium">No high-risk patients</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Checks */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Recent Interaction Checks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Patient</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">New Medication</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Interactions</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Max Severity</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.recent_checks?.map((check) => (
                        <tr key={check.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-semibold text-slate-900">{check.patient_name}</td>
                          <td className="px-4 py-3 text-slate-700 font-medium">{check.new_medication}</td>
                          <td className="px-4 py-3 text-slate-900 font-bold">{check.interactions_found}</td>
                          <td className="px-4 py-3">{getSeverityBadge(check.max_severity)}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 font-medium">
                            {new Date(check.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alerts" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Active Drug Interaction Alerts</CardTitle>
                <CardDescription className="text-slate-600 font-medium">Alerts requiring review and acknowledgment</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${alert.severity === 'major' ? 'bg-red-100' : 'bg-orange-100'}`}>
                          <AlertTriangle className={`h-5 w-5 ${alert.severity === 'major' ? 'text-red-600' : 'text-orange-600'}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{alert.patient_name}</p>
                          <p className="text-sm text-slate-600 font-medium">
                            {alert.drugs_involved?.join(' + ')}
                          </p>
                          <p className="text-xs text-slate-500 font-medium mt-1">
                            {new Date(alert.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getSeverityBadge(alert.severity)}
                        <Button size="sm" variant="outline" onClick={() => acknowledgeAlert(alert.id)}>
                          Acknowledge
                        </Button>
                      </div>
                    </div>
                  ))}
                  {alerts.length === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">No active alerts</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="database" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Drug Database</CardTitle>
                <CardDescription className="text-slate-600 font-medium">Available drugs with their classifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {drugs.map((drug) => (
                    <div key={drug.id} className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <Pill className="h-5 w-5 text-teal-600" />
                        <span className="font-semibold text-slate-900">{drug.name}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {drug.classes?.map((cls) => (
                          <Badge key={cls} variant="outline" className="text-xs font-medium">
                            {cls.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Check Interactions Dialog */}
      <Dialog open={showCheckDialog} onOpenChange={setShowCheckDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-bold">Check Drug Interactions</DialogTitle>
            <DialogDescription className="text-slate-600 font-medium">
              Enter current medications and a new medication to check for interactions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-semibold text-slate-900">Patient Name (Optional)</Label>
              <Input
                placeholder="Enter patient name"
                value={checkForm.patient_name}
                onChange={(e) => setCheckForm({ ...checkForm, patient_name: e.target.value })}
              />
            </div>
            <div>
              <Label className="font-semibold text-slate-900">Current Medications</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Add medication..."
                  value={newMedInput}
                  onChange={(e) => setNewMedInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCurrentMedication()}
                />
                <Button variant="outline" onClick={addCurrentMedication}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {checkForm.current_medications.map((med) => (
                  <Badge key={med} variant="secondary" className="font-medium">
                    {med}
                    <button className="ml-2 text-xs" onClick={() => removeMedication(med)}>×</button>
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label className="font-semibold text-slate-900">New Medication to Check</Label>
              <Select
                value={checkForm.new_medication}
                onValueChange={(value) => setCheckForm({ ...checkForm, new_medication: value })}
              >
                <SelectTrigger><SelectValue placeholder="Select medication" /></SelectTrigger>
                <SelectContent>
                  {drugs.map((drug) => (
                    <SelectItem key={drug.id} value={drug.name}>{drug.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Results */}
            {checkResults && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-bold text-slate-900">Results:</span>
                  {getSeverityBadge(checkResults.max_severity)}
                </div>
                {checkResults.interactions?.length > 0 ? (
                  <div className="space-y-3">
                    {checkResults.interactions.map((interaction, idx) => (
                      <div key={idx} className="p-3 bg-white rounded border">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-slate-900">{interaction.drug1}</span>
                          <span className="text-slate-500">+</span>
                          <span className="font-semibold text-slate-900">{interaction.drug2}</span>
                          {getSeverityBadge(interaction.severity)}
                        </div>
                        <p className="text-sm text-slate-700 font-medium mb-1">{interaction.effect}</p>
                        <p className="text-sm text-slate-600"><strong>Recommendation:</strong> {interaction.recommendation}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {interaction.monitoring?.map((item, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{item}</Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">No interactions detected</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCheckDialog(false);
              setCheckResults(null);
              setCheckForm({ patient_id: '', patient_name: '', current_medications: [], new_medication: '' });
            }}>
              Close
            </Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={checkInteractions}>
              <Search className="h-4 w-4 mr-2" />
              Check Interactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicationInteractionChecker;
